import { useId, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import {
	CoreModal,
	CorePaper,
	CoreRadioCard,
	CoreRadioCardSelectIcon,
	CoreRequiredIcon,
	CoreTextField,
} from "../../core";
import styles from "./selectAccommodationModal.module.css";
import i18nPackage from "../../i18n/i18n";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import { EditQuizExpandButton } from "../components/EditQuizButton";
import { useEditQuizStyles } from "../components";
import ProfileList, { ProfileListProps } from "../profile/ProfileList";
import { ResponsiveModalBody } from "../components/EditQuizModalComponents";

type ProctorButtonProps = {
	checked: boolean;
	onClick: () => void;
	title: string;
	description: string;
};

function ProctorButton({ checked, description, onClick, title }: ProctorButtonProps) {
	return (
		<CoreRadioCard
			onClick={onClick}
			selected={checked}
			slotProps={{
				button: { className: styles.proctorButton },
				container: { className: styles.proctorButtonContainer },
			}}
		>
			<div className={styles.proctorInfoCol}>
				<Typography className={styles.title}>{title}</Typography>
				<Typography className={styles.description}>{description}</Typography>
			</div>
			<CoreRadioCardSelectIcon selected={checked} />
		</CoreRadioCard>
	);
}

type UseAccommodationNotesProps = {
	studentAccommodationNotes: SelectAccommodationProps["studentAccommodationNotes"];
};

function useAccommodationNotes({ studentAccommodationNotes }: UseAccommodationNotesProps) {
	const [selectedNotes, setSelectedNotes] = useState(studentAccommodationNotes || undefined);
	const notesComponent = (
		<EditQuizExpandButton
			title={i18nPackage.t("editQuizPage.accommodationModal.notesHeader")}
			description={i18nPackage.t("editQuizPage.accommodationModal.notesLabel")}
			defaultOpen={true}
			slotProps={{
				title: {
					className: styles.setSectionHeader,
					style: {
						borderBottom: "0",
					},
				},
				description: {
					className: styles.setSectionContentHeader,
				},
			}}
		>
			<CoreTextField
				value={selectedNotes}
				label={i18nPackage.t("editQuizPage.accommodationModal.textFieldLabel")}
				onChange={(e) => setSelectedNotes(e.target.value)}
				className={styles.notesSectionContentTextBox}
				multiline
				InputProps={{
					inputComponent: "textarea",
				}}
			/>
		</EditQuizExpandButton>
	);

	return {
		notesComponent,
		selectedNotes,
	};
}

type ProfileSectionProps = {
	isMobileLayout: boolean;
} & Pick<
	ProfileListProps,
	| "profiles"
	| "selectedId"
	| "onProfileSelected"
	| "optionRelatedInstituteSettings"
	| "verityInstituteSettings"
	| "enforcedProfilesOnly"
	| "proctoringData"
	| "canDeleteProfile"
	| "onDelete"
>;

function ProfileSection({ isMobileLayout, ...props }: ProfileSectionProps) {
	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper component="section" className={styles.profileSection}>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => <div className={styles.setSectionContent}>{children}</div>}
		>
			<Typography variant="h2" className={styles.setSectionHeader}>
				{i18nPackage.t("editQuizPage.accommodationModal.addProfileHeader")}
				<span style={{ marginLeft: "0.3125rem" }}>
					<CoreRequiredIcon />
				</span>
			</Typography>
			<Typography className={styles.setSectionContentHeader}>
				{i18nPackage.t("editQuizPage.accommodationModal.addProfileLabel")}
			</Typography>
			<div className={styles.profileSectionContentList}>
				<ProfileList {...props} showCustomButton={false} />
			</div>
		</ConditionalWrapper>
	);
}

export type SelectAccommodationProps = {
	activeProfileId: number | undefined;
	studentAccommodationNotes: string | undefined;
	saveHandler: (selectedProfileId: number | undefined, selectedNotes: string | undefined) => void;
	onClose: () => void;
} & Pick<
	ProfileSectionProps,
	| "enforcedProfilesOnly"
	| "optionRelatedInstituteSettings"
	| "verityInstituteSettings"
	| "proctoringData"
	| "canDeleteProfile"
	| "onDelete"
	| "profiles"
>;

const SelectAccommodationModal = ({
	activeProfileId,
	studentAccommodationNotes,
	saveHandler,
	onClose,
	...props
}: SelectAccommodationProps) => {
	const [selectedProfileId, setSelectedProfileId] = useState(activeProfileId);
	const [useProfile, setUseProfile] = useState(!!activeProfileId);
	const isMobileLayout = useMobileLayout();

	const { notesComponent, selectedNotes } = useAccommodationNotes({ studentAccommodationNotes });
	const { getEditQuizModalWidth, getModalInModalProps } = useEditQuizStyles();

	const handleUseProfileToggle = (val: boolean) => {
		if (val !== useProfile) {
			if (useProfile) {
				setSelectedProfileId(undefined);
			}
			setUseProfile(!useProfile);
		}
	};

	const profileSectionProps = useMemo((): ProfileSectionProps => {
		return {
			...props,
			isMobileLayout,
			onProfileSelected: setSelectedProfileId,
			selectedId: selectedProfileId,
		};
	}, [isMobileLayout, props, selectedProfileId]);

	const accommodationRadioAriaId = useId();

	const title = i18nPackage.t("editQuizPage.accommodationModal.title");

	return (
		<CoreModal
			{...getEditQuizModalWidth(isMobileLayout)}
			{...getModalInModalProps(isMobileLayout, title)}
			open={true}
			onClose={() => {}}
			onTopClose={onClose}
			padding={isMobileLayout ? undefined : "16px 16px 4px 16px"}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.accommodationModal.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18nPackage.t("editQuizPage.accommodationModal.save"),
					color: "primary",
					onClick: () => {
						saveHandler(selectedProfileId, selectedNotes);
						onClose();
					},
					disabled: useProfile && !selectedProfileId,
				},
			]}
		>
			<ResponsiveModalBody
				title={title}
				description={i18nPackage.t("editQuizPage.accommodationModal.description")}
				contentClassName={styles.modalPage}
			>
				<div className={styles.setSectionContent}>
					<Typography variant="h2" className={styles.setSectionHeader}>
						{i18nPackage.t("editQuizPage.accommodationModal.setAccommodation")}
					</Typography>
					<Typography className={styles.setSectionContentHeader} id={accommodationRadioAriaId}>
						{i18nPackage.t("editQuizPage.accommodationModal.section1Header")}
					</Typography>
					<div className={styles.setSectionContentOptions}>
						<ProctorButton
							title={i18nPackage.t("editQuizPage.accommodationModal.doNotProctorTitle")}
							description={i18nPackage.t("editQuizPage.accommodationModal.doNotProctorLabel")}
							onClick={() => handleUseProfileToggle(false)}
							checked={!useProfile}
						/>
						<ProctorButton
							title={i18nPackage.t("editQuizPage.accommodationModal.modifyTitle")}
							description={i18nPackage.t("editQuizPage.accommodationModal.modifyLabel")}
							onClick={() => handleUseProfileToggle(true)}
							checked={useProfile}
						/>
					</div>
				</div>
				{isMobileLayout ? (
					<>
						{notesComponent}
						{useProfile && <ProfileSection {...profileSectionProps} />}
					</>
				) : (
					<>
						{useProfile && <ProfileSection {...profileSectionProps} />}
						{notesComponent}
					</>
				)}
			</ResponsiveModalBody>
		</CoreModal>
	);
};

export default SelectAccommodationModal;
